import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TwilioLifeIdVideoSC } from './twilio-video.styled';
import { useIntl } from 'react-intl';
import TStore from '../../../../types/store';
import Video from 'twilio-video';
import { Participant } from './participant/participant';
import { useErrorHandling, useParticipantNames } from './twilio-video.utils';
import PermissionError from './permission-error';
import { EVisitItem } from '../../../../types/e-visits';
import { actions } from '../../../../store/ducks/twilio';
import { actions as eVisitAction } from '../../../../store/ducks/e-visits';

interface TwilioLifeIdVideoProps {
  item: EVisitItem;
  className?: string;
}

/**
 * https://www.twilio.com/blog/video-chat-react-hooks
 */
export const TwilioLifeIdVideo: React.FunctionComponent<TwilioLifeIdVideoProps> = ({
  item,
  className,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [room, setRoom] = useState<null | any>(null);
  const [participants, setParticipants] = useState<Video.RemoteParticipant[]>([]);

  const twilioRoomData = useSelector((state: TStore) => state.twilio.twilioRoomData);
  const localDisplayMode = useSelector((state: TStore) => state.twilio.localDisplayMode);
  const permissionError = useSelector((state: TStore) => state.twilio.permissionError);

  const { onError } = useErrorHandling(dispatch, formatMessage);
  const { localParticipantName, remoteParticipantName } = useParticipantNames(dispatch, item);

  window.addEventListener('beforeunload', () => {
    room.disconnect();
    dispatch(eVisitAction.setSingleEvent());
  });

  window.addEventListener('pagehide', () => {
    room.disconnect();
    dispatch(eVisitAction.setSingleEvent());
  });

  useEffect(() => {
    if (!twilioRoomData || permissionError) return;

    const participantConnected = (participant: Video.RemoteParticipant) => {
      setParticipants((prevParticipants) => [...prevParticipants, participant]);
    };
    const participantDisconnected = (participant: Video.RemoteParticipant) => {
      setParticipants((prevParticipants) => prevParticipants.filter((p) => p !== participant));
    };

    Video.connect(twilioRoomData.token, {
      name: item.event.title || item.event.description || 'twilio-room',
    })
      .then((room) => {
        setRoom(room);
        room.on('participantConnected', participantConnected);
        room.on('participantDisconnected', participantDisconnected);
        room.on('reconnecting', (error) => {
          onError(error, 'reconnecting');
        });
        room.participants.forEach(participantConnected);
      })
      .catch((error) => {
        onError(error, 'create');
      });

    return () => {
      setRoom((currentRoom: any) => {
        if (currentRoom && currentRoom.localParticipant.state === 'connected') {
          currentRoom.localParticipant.tracks.forEach(function (trackPublication: any) {
            trackPublication.track.stop();
          });
          currentRoom.disconnect();
          dispatch(eVisitAction.setSingleEvent());
          return null;
        } else {
          return currentRoom;
        }
      });
      dispatch(actions.resetTwilioRoom());
    };
  }, [twilioRoomData, permissionError, item, dispatch, setRoom]);

  const remoteParticipants = participants.length ? (
    participants.map((participant) => (
      <Participant
        className={className ? className : ''}
        key={participant.sid}
        participant={participant}
        type={'remote'}
        displayMode={localDisplayMode === 'full' ? 'window' : 'full'}
        name={remoteParticipantName}
      />
    ))
  ) : (
    <div className={className ? `empty-participant ${className}` : 'empty-participant'} />
  );

  return (
    <TwilioLifeIdVideoSC>
      <div className="local-participant">
        {room ? (
          <Participant
            className={className ? className : ''}
            key={room.localParticipant.sid}
            participant={room.localParticipant as Video.RemoteParticipant}
            type={'local'}
            displayMode={localDisplayMode}
            name={localParticipantName}
            room={room}
          />
        ) : null}
      </div>
      <div className="remote-participants">{remoteParticipants}</div>
      <PermissionError />
    </TwilioLifeIdVideoSC>
  );
};

export default React.memo(TwilioLifeIdVideo);
